import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Monitor, FileText, Wand2, Video, Lock, Crown } from 'lucide-react';

import { StreamShareTab } from '../components/creator/StreamShareTab';
import { WeeklyContentTab } from '../components/creator/WeeklyContentTab';
import { AIStudioTab } from '../components/creator/AIStudioTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreatorHub = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('stream');
  const [loading, setLoading] = useState(true);

  // Stream settings (shared with StreamShareTab)
  const [settings, setSettings] = useState(null);

  // Weekly content (shared with WeeklyContentTab)
  const [recap, setRecap] = useState(null);
  const [quickStats, setQuickStats] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    if (!user) { navigate('/'); return; }
    fetchSettings();
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/creator/share-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeeklyContent = async () => {
    if (recap) return;
    setContentLoading(true);
    try {
      const [recapRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/weekly-recap`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/content/quick-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (recapRes.ok) setRecap(await recapRes.json());
      if (statsRes.ok) setQuickStats(await statsRes.json());
    } catch {
      toast.error("Failed to load content data");
    } finally {
      setContentLoading(false);
    }
  };

  const refreshWeeklyContent = async () => {
    setContentLoading(true);
    try {
      const [recapRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/content/weekly-recap`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/content/quick-stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (recapRes.ok) setRecap(await recapRes.json());
      if (statsRes.ok) setQuickStats(await statsRes.json());
      toast.success("Content refreshed!");
    } catch {
      toast.error("Failed to refresh content");
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch weekly content when switching to content tab
  useEffect(() => {
    if (activeTab === 'content' && !recap && !contentLoading) {
      fetchWeeklyContent();
    }
  }, [activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  // Pro gate
  const isAdmin = user?.email === 'philipreardon121@gmail.com';
  const isPro = user?.is_pro || isAdmin;

  if (!isPro) {
    return (
      <div className="min-h-screen bg-slate-950 pt-20 pb-12 px-4" data-testid="creator-hub-locked">
        <div className="max-w-lg mx-auto mt-16">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Crown className="w-8 h-8 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-3">Creator Hub is Pro Only</h2>
              <p className="text-slate-400 mb-6 max-w-sm mx-auto">
                Unlock streaming widgets, AI-powered script generation, weekly content tools, and more with a Pro subscription.
              </p>
              <div className="space-y-3 text-left max-w-xs mx-auto mb-8">
                {['OBS-ready streaming widgets', 'AI YouTube Script Generator', 'Weekly content & social posts',
                  'Webhook trade alerts', 'Script history (14-day storage)'].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-slate-300 text-sm">
                    <Lock className="w-4 h-4 text-amber-400 shrink-0" /> {feature}
                  </div>
                ))}
              </div>
              <Button onClick={() => navigate('/settings')} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold" data-testid="upgrade-to-pro-btn">
                <Crown className="w-4 h-4 mr-2" /> Upgrade to Pro
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 pt-20 pb-12 px-4" data-testid="creator-hub">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Video className="w-8 h-8 text-emerald-400" /> Creator Hub
          </h1>
          <p className="text-slate-400 mt-2">Everything you need to stream, create content, and grow your audience</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-800/50 border border-slate-700 p-1">
            <TabsTrigger value="stream" className="data-[state=active]:bg-emerald-600" data-testid="tab-stream">
              <Monitor className="w-4 h-4 mr-2" /> Stream & Share
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-emerald-600" data-testid="tab-content">
              <FileText className="w-4 h-4 mr-2" /> Weekly Content
            </TabsTrigger>
            <TabsTrigger value="ai-studio" className="data-[state=active]:bg-emerald-600" data-testid="tab-ai-studio">
              <Wand2 className="w-4 h-4 mr-2" /> AI Studio
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stream" className="space-y-6">
            <StreamShareTab user={user} token={token} settings={settings} fetchSettings={fetchSettings} />
          </TabsContent>

          <TabsContent value="content" className="space-y-6">
            <WeeklyContentTab
              token={token} recap={recap} quickStats={quickStats}
              contentLoading={contentLoading} fetchWeeklyContent={fetchWeeklyContent}
              refreshWeeklyContent={refreshWeeklyContent}
            />
          </TabsContent>

          <TabsContent value="ai-studio" className="space-y-6">
            <AIStudioTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CreatorHub;
